import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import {
  BellIcon,
  ClockIcon,
  CheckIcon,
  XMarkIcon,
  EnvelopeIcon,
  UserGroupIcon,
  FlagIcon,
  PlusIcon,
  PencilIcon,
} from '@heroicons/react/24/outline'
import type { FollowUp, FollowUpType } from '../../types'
import { CollapsibleSection } from './CollapsibleSection'

type FollowUpItem = {
  followUp: FollowUp
  daysUntil: number
  isOverdue: boolean
  isToday: boolean
  isTomorrow: boolean
}

type Props = {
  followUps: FollowUp[]
  onMarkDone: (followUpId: string) => void
  onDelete: (followUpId: string) => void
  onEdit: (followUp: FollowUp) => void
  onAddNew: (applicationId?: string) => void
  isLoading?: boolean
}

const getUrgencyClass = (item: FollowUpItem): string => {
  if (item.isOverdue) return 'border-l-rose-500 bg-rose-50 dark:bg-rose-950/20'
  if (item.isToday) return 'border-l-amber-500 bg-amber-50 dark:bg-amber-950/20'
  if (item.isTomorrow) return 'border-l-sky-500 bg-sky-50 dark:bg-sky-950/20'
  return 'border-l-app-line bg-app-surface/30'
}

const getUrgencyBadge = (item: FollowUpItem): { text: string; className: string } => {
  if (item.isOverdue) {
    return {
      text: item.daysUntil === -1 ? 'Yesterday' : `${Math.abs(item.daysUntil)} days overdue`,
      className: 'bg-rose-50 dark:bg-rose-900/50 text-rose-700 dark:text-rose-300',
    }
  }
  if (item.isToday) {
    return { text: 'Today', className: 'bg-amber-50 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300' }
  }
  if (item.isTomorrow) {
    return { text: 'Tomorrow', className: 'bg-sky-50 dark:bg-sky-900/50 text-sky-700 dark:text-sky-300' }
  }
  return {
    text: `In ${item.daysUntil} days`,
    className: 'bg-app-surface text-app-muted',
  }
}

const getTypeIcon = (type: FollowUpType) => {
  switch (type) {
    case 'interview':
      return UserGroupIcon
    case 'email':
      return EnvelopeIcon
    case 'deadline':
      return FlagIcon
    default:
      return ClockIcon
  }
}

const formatDate = (dateStr: string): string => {
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  })
}

const formatTime = (dateStr: string): string => {
  const date = new Date(dateStr)
  return date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  })
}

export const FollowUpsSection = ({
  followUps,
  onMarkDone,
  onDelete,
  onEdit,
  onAddNew,
  isLoading,
}: Props) => {
  const followUpItems = useMemo(() => {
    const now = new Date()
    now.setHours(0, 0, 0, 0)
    const today = now.getTime()

    return followUps
      .filter((fu) => !fu.completed)
      .map((followUp) => {
        const dueDate = new Date(followUp.dueAt)
        dueDate.setHours(0, 0, 0, 0)
        const diff = dueDate.getTime() - today
        const daysUntil = Math.round(diff / (1000 * 60 * 60 * 24))

        return {
          followUp,
          daysUntil,
          isOverdue: daysUntil < 0,
          isToday: daysUntil === 0,
          isTomorrow: daysUntil === 1,
        }
      })
      .sort((a, b) => a.daysUntil - b.daysUntil)
  }, [followUps])

  // Filter to show only relevant follow-ups (within next 14 days or overdue)
  const visibleItems = useMemo(() => {
    return followUpItems.filter((item) => item.daysUntil <= 14).slice(0, 8)
  }, [followUpItems])

  if (isLoading) {
    return (
      <CollapsibleSection
        title="Follow-ups"
        icon={BellIcon}
        storageKey="dashboard-followups-expanded"
        defaultExpanded={true}
      >
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="border-l-2 border-l-app-line bg-app-surface/30 p-3 animate-pulse rounded-2xl shadow-app-card">
              <div className="flex items-start gap-3">
                <div className="h-8 w-8 rounded-lg bg-app-surface" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-32 bg-app-surface rounded" />
                  <div className="h-3 w-24 bg-app-surface rounded" />
                </div>
              </div>
            </div>
          ))}
        </div>
      </CollapsibleSection>
    )
  }

  if (visibleItems.length === 0) {
    return (
      <CollapsibleSection
        title="Follow-ups"
        icon={BellIcon}
        storageKey="dashboard-followups-expanded"
        defaultExpanded={true}
        headerAction={
          <button
            onClick={(e) => {
              e.stopPropagation()
              onAddNew()
            }}
            className="p-1 text-app-muted hover:text-app-muted hover:bg-app-surface transition-colors rounded-lg"
            title="Add reminder"
          >
            <PlusIcon className="h-4 w-4" />
          </button>
        }
      >
        <div className="border border-dashed border-app-line bg-app-surface/30 p-6 text-center rounded-2xl shadow-app-card">
          <BellIcon className="mx-auto h-8 w-8 text-app-muted mb-2" />
          <p className="text-sm text-app-muted mb-3">No upcoming follow-ups</p>
          <p className="text-xs text-app-muted mb-4">
            Set reminders for your applications to stay on top of your job search.
          </p>
          <button
            onClick={() => onAddNew()}
            className="inline-flex items-center gap-1.5 rounded-lg bg-app-primary px-3 py-2 text-xs font-medium text-white hover:bg-app-primary-hover transition-colors"
          >
            <PlusIcon className="h-4 w-4" />
            Add reminder
          </button>
        </div>
      </CollapsibleSection>
    )
  }

  return (
    <CollapsibleSection
      title="Follow-ups"
      icon={BellIcon}
      storageKey="dashboard-followups-expanded"
      defaultExpanded={true}
      headerAction={
        <button
          onClick={(e) => {
            e.stopPropagation()
            onAddNew()
          }}
          className="p-1 text-app-muted hover:text-app-muted hover:bg-app-surface transition-colors rounded-lg"
          title="Add reminder"
        >
          <PlusIcon className="h-4 w-4" />
        </button>
      }
    >
      <div className="space-y-2">
        {visibleItems.map((item) => {
          const badge = getUrgencyBadge(item)
          const TypeIcon = getTypeIcon(item.followUp.type)

          return (
            <div
              key={item.followUp.id}
              className={`group relative rounded-lg border-l-2 ${getUrgencyClass(item)} p-3 transition-colors hover:bg-app-surface/50`}
            >
              <div className="flex items-start gap-3">
                {/* Icon */}
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-app-surface">
                  <TypeIcon className="h-4 w-4 text-app-muted" />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start gap-2 mb-0.5">
                    <span className="font-medium text-app-muted text-sm leading-tight">
                      {item.followUp.title}
                    </span>
                    <span
                      className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${badge.className}`}
                    >
                      {badge.text}
                    </span>
                  </div>
                  {item.followUp.application && (
                    <Link
                      to={`/app/${item.followUp.applicationId}`}
                      className="text-xs text-app-accent/80 hover:text-app-accent-hover truncate block"
                    >
                      {item.followUp.application.company}
                      {item.followUp.application.positionTitle && (
                        <span className="text-app-muted"> · {item.followUp.application.positionTitle}</span>
                      )}
                    </Link>
                  )}
                  {item.followUp.note && (
                    <p className="text-xs text-app-muted mt-1 line-clamp-1">
                      {item.followUp.note}
                    </p>
                  )}
                  <p className="text-xs text-app-muted mt-1">
                    {formatDate(item.followUp.dueAt)} at {formatTime(item.followUp.dueAt)}
                  </p>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => onMarkDone(item.followUp.id)}
                    className="p-1.5 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 transition-colors rounded-lg"
                    title="Mark done"
                    aria-label="Mark follow-up as done"
                  >
                    <CheckIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => onEdit(item.followUp)}
                    className="p-1.5 text-app-muted hover:bg-app-surface transition-colors rounded-lg"
                    title="Edit"
                    aria-label="Edit follow-up"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => onDelete(item.followUp.id)}
                    className="p-1.5 text-app-muted hover:bg-app-surface hover:text-app-muted transition-colors rounded-lg"
                    title="Delete"
                    aria-label="Delete follow-up"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          )
        })}

        {followUpItems.length > visibleItems.length && (
          <p className="text-xs text-app-muted text-center pt-2">
            +{followUpItems.length - visibleItems.length} more follow-ups scheduled
          </p>
        )}
      </div>
    </CollapsibleSection>
  )
}
